import React, { ChangeEvent, useContext, useEffect, useState } from 'react';
import { Components } from './styled';
import ProjectModal from '../../ProjectModal';
import { textFieldUtils } from '../../../utils/textFields';
import ProjectTextField from '../../inputs/ProjectTextField';
import { Formik, FormikValues } from 'formik';
import { MinimalLessonSchema } from '../../../api/lesson/parser';
import { CreateLessonDto } from '../../../api/typeDtos/lesson';
import { ActivityIndicatorContext } from '../../../providers/ActivityIndicatorProvider/context';
import { createLesson, updateLesson } from '../../../api/lesson';
import { isProjectError } from '../../../utils/errors';
import { ToastContext } from '../../../providers/ToastProvider/context';
import { buildDefaultPostTitle, Lesson, POLL_DEFAULT_OPTIONS } from '../../../api/types/lesson';
import { ReactComponent as AccessLinkIcon } from '../../../assets/icons/icon-grey-check-square-offset.svg';
import AddKeyTakeaways from './components/AddKeyTakeaways';
import AddPoll from './components/AddPoll';
import { buildPollForSave, isPollEmpty, PollValidationErrors, validatePoll } from './components/AddPoll/utils';
import { getPresignedUrl, uploadPrivateFile, uploadPublicFile } from '../../../utils/s3';
import { getFileExtension } from '../../../utils/s3/utils';
import { getLessonUploadFileName } from '../../../api/lesson/utils';
import ProjectCalendarPicker from '../../dateAndTime/ProjectCalendarPicker';
import { DAILY_SESSION_NETWORK_ID, DAILY_SESSION_NETWORK_NAME, PUBLIC_BUCKET } from '../../../defines';
import { AppSettingsContext } from '../../../providers/AppSettingsProvider/context';
import useModalState from '../../../hooks/useModalState';
import ConfirmationModal from '../ConfirmationModal';
import { confirmationTexts } from '../ConfirmationModal/utils';
import { getPastMidnightUTC } from '../../../utils/dateFormat';
import { getTextFromSelectedItems } from '../../../hooks/useSearchDropdown';
import ProjectSearchDropdown from '../../ProjectSearchDropdown';
import { getPossibleCategories, getPossiblePillars } from '../../../api/contentManagement/utils';
import useCustomizedDropdown from '../../../hooks/useCustomizedDropdown';
import ContentInsightsCard from '../../ContentInsightsCard';
import { Tag, TAGS } from '../../../api/types/others';
import LessonTranscriptSection from './LessonTranscriptSection';
import { normalizeLrc } from '../../../utils/transcript/lrc';

const initialValues = {
  title: '',
  lengthMinutes: '',
  lengthSeconds: '',
  category: '',
  pillarType: '',
  pillarExpertise: '',
  isDailyAlux: true,
  isQuestionAndAnswer: false,
  tags: [],
  lrcTranscript: '',
  listenReason: '',
  postTitle: '',
  postContent: '',
};

// Prefill for a brand-new lesson (e.g. created from an author's course).
export type LessonDefaultValues = { isDailyAlux?: boolean; isQuestionAndAnswer?: boolean };

const ModalBody = ({
  modalState,
  publishedAtDate,
  defaultValues,
  onSave,
  onCancel,
  isModalOpen,
}: {
  modalState?: Lesson;
  publishedAtDate?: Date;
  defaultValues?: LessonDefaultValues;
  onSave: (values: FormikValues) => Promise<void>;
  onCancel: () => void;
  isModalOpen: boolean;
}) => {
  const { setIsLoading } = useContext(ActivityIndicatorContext);
  const { categories, pillarTypes, pillarExpertises, goals } = useContext(AppSettingsContext);

  const [formikValues, setFormikValues] = useState<FormikValues>();
  const [formState, setFormState] = useState<Lesson>();

  const [possiblePillars, setPossiblePillars] = useState(pillarTypes);
  const [possibleCategories, setPossibleCategories] = useState(categories);

  const [audioFilePath, setAudioFilePath] = useState<string | undefined>(modalState?.audioStreamPath || undefined);
  const [audioUploadProgress, setAudioUploadProgress] = useState<number | undefined>(undefined);

  const [videoFilePath, setVideoFilePath] = useState<string | undefined>(modalState?.videoStreamPath || undefined);
  const [videoUploadProgress, setVideoUploadProgress] = useState<number | undefined>(undefined);

  const [imagePath, setImagePath] = useState<string | undefined>(modalState?.imagePath || undefined);
  const [imageUrl, setImageUrl] = useState<string | undefined>();
  const [imageUploadProgress, setImageUploadProgress] = useState<number | undefined>(undefined);

  const [keyTakeaways, setKeyTakeaways] = useState<string[]>(modalState?.keyTakeaways || []);

  const [pollQuestion, setPollQuestion] = useState<string>(modalState?.poll?.question || '');
  const [pollOptions, setPollOptions] = useState<string[]>(
    modalState?.poll?.options?.length ? modalState.poll.options : new Array(POLL_DEFAULT_OPTIONS).fill(''),
  );
  const [pollErrors, setPollErrors] = useState<PollValidationErrors | undefined>();
  // Only enforced for a brand-new daily session; existing ones may not have it yet.
  const [listenReasonError, setListenReasonError] = useState<string | undefined>();
  const isNewLesson = !modalState?.id;
  // Options already persisted on the lesson may have votes recorded against their index.
  const lockedOptionsCount = modalState?.poll?.options?.length || 0;

  const [displayPublishDate, setDisplayPublishDate] = useState<Date | null>(
    publishedAtDate || modalState?.publishedAtDate || null,
  );
  const [publishDate, setPublishDate] = useState<Date | null>(publishedAtDate || modalState?.publishedAtDate || null);

  const { props: categoryDropdownProps, setProps: setCategoryDropdownProps } = useCustomizedDropdown({
    possibleValues: categories,
    singleSelect: true,
  });
  const { props: pillarTypeDropdownProps, setProps: setPillarTypeDropdownProps } = useCustomizedDropdown({
    possibleValues: pillarTypes,
    singleSelect: true,
    customType: 'pillarType',
  });
  const { props: pillarExpertiseDropdownProps, setProps: setPillarExpertiseDropdownProps } = useCustomizedDropdown({
    possibleValues: pillarExpertises,
    singleSelect: true,
    customType: 'pillarExpertise',
  });

  const [tagsChoices, setTagsChoices] = useState<Tag[]>(TAGS);
  const { props: tagsDropdownProps, setProps: setTagsDropdownProps } = useCustomizedDropdown({
    possibleValues: tagsChoices.map((item) => item?.value),
    customType: 'tag',
  });

  const lessonInitialValues = {
    title: modalState?.title,
    lengthMinutes: modalState?.totalLength ? `${Math.floor(modalState?.totalLength / 60)}` : '',
    lengthSeconds: modalState?.totalLength ? `${modalState?.totalLength % 60}` : '',
    category: modalState?.category,
    pillarType: modalState?.pillarType,
    pillarExpertise: modalState?.pillarExpertise,
    isDailyAlux: modalState?.isDailyLesson,
    isQuestionAndAnswer: modalState?.isQuestionAndAnswer || false,
    tags: modalState?.tags,
    lrcTranscript: modalState?.lrcTranscript ? normalizeLrc(modalState.lrcTranscript) : '',
    listenReason: modalState?.listenReason || '',
    postTitle: modalState?.postTitle || '',
    postContent: modalState?.postContent || '',
  };

  useEffect(() => {
    if (lessonInitialValues?.category && lessonInitialValues?.category?.length > 0) {
      setCategoryDropdownProps([lessonInitialValues?.category]);
    }
    if (lessonInitialValues?.pillarType && lessonInitialValues?.pillarType?.length > 0) {
      setPillarTypeDropdownProps([lessonInitialValues?.pillarType]);
    }
    if (lessonInitialValues?.pillarExpertise && lessonInitialValues?.pillarExpertise?.length > 0) {
      setPillarExpertiseDropdownProps([lessonInitialValues?.pillarExpertise]);
    }

    if (Array.isArray(lessonInitialValues?.tags) && lessonInitialValues?.tags?.length) {
      const allTags = TAGS;
      lessonInitialValues?.tags?.forEach((value) => {
        const matchingTag = TAGS.filter((item) => item.value === value)?.[0];
        if (!matchingTag) {
          allTags.push({ value: value, text: value });
        }
      });
      setTagsChoices(allTags);
      setTagsDropdownProps(lessonInitialValues?.tags);
    }
  }, []);

  useEffect(() => {
    const selectedPillar = pillarTypeDropdownProps.selectedItems?.[0];
    if (!!selectedPillar?.value) {
      setPossibleCategories(getPossibleCategories(goals, selectedPillar?.value));
    } else {
      setPossibleCategories(categories);
    }
  }, [pillarTypeDropdownProps.selectedItems]);

  useEffect(() => {
    const selectedCategory = categoryDropdownProps.selectedItems?.[0];
    if (!!selectedCategory?.value) {
      setPossiblePillars(getPossiblePillars(goals, selectedCategory?.value));
    } else {
      setPossiblePillars(pillarTypes);
    }
  }, [categoryDropdownProps.selectedItems]);

  useEffect(() => {
    setFormState({
      ...formikValues,
      keyTakeaways,
      imagePath,
      isRagsToRichesStory: modalState?.isRagsToRichesStory,
      isSleepStory: modalState?.isSleepStory,
      isSpecialStory: modalState?.isSpecialStory,
      isWisdomStory: modalState?.isWisdomStory,
      category: categoryDropdownProps?.selectedItems?.map((item) => item.value)?.[0],
      pillarType: pillarTypeDropdownProps?.selectedItems?.map((item) => item.value)?.[0],
      pillarExpertise: pillarExpertiseDropdownProps?.selectedItems?.map((item) => item.value)?.[0],
      tags: tagsDropdownProps?.selectedItems?.map((item) => item.value),
    } as Lesson);
  }, [
    formikValues,
    keyTakeaways,
    imagePath,
    categoryDropdownProps.selectedItems,
    pillarTypeDropdownProps.selectedItems,
    pillarExpertiseDropdownProps.selectedItems,
    tagsDropdownProps.selectedItems,
  ]);

  const onUploadPrivateFile = async (
    e: ChangeEvent<HTMLInputElement> | undefined,
    fileName: string,
    path: string,
    source: 'audioStreamPath' | 'videoStreamPath',
  ) => {
    setIsLoading(true);
    const file = e?.target?.files?.[0];
    // @ts-ignore
    const newFile = new File([file], fileName, { type: file?.type });
    if (newFile) {
      if (e && e.target && e.target.value) {
        e.target.value = '';
      }

      await uploadPrivateFile(newFile, path, (progress) => {
        if (source === 'audioStreamPath') {
          setAudioUploadProgress(progress);
        } else {
          setVideoUploadProgress(progress);
        }

        if (progress === 100) {
          if (source === 'audioStreamPath') {
            setAudioFilePath(path);
            setAudioUploadProgress(undefined);
          } else {
            setVideoFilePath(path);
            setVideoUploadProgress(undefined);
          }
        }
      });
    }
    setIsLoading(false);
  };

  const onUploadPublicFile = async (e: ChangeEvent<HTMLInputElement> | undefined, fileName: string, path: string) => {
    setIsLoading(true);
    const file = e?.target?.files?.[0];
    // @ts-ignore
    const newFile = new File([file], fileName, { type: file?.type });
    if (newFile) {
      if (e && e.target && e.target.value) {
        e.target.value = '';
      }

      await uploadPublicFile(newFile, path, (progress) => {
        setImageUploadProgress(progress);
        if (progress === 100) {
          setImagePath(path);
          const link = PUBLIC_BUCKET + path;
          setImageUrl(link);
          setImageUploadProgress(undefined);
        }
      });
    }
    setIsLoading(false);
  };

  const selectHandlerForPrivateFile = async (
    event: React.ChangeEvent<HTMLInputElement> | undefined,
    source: 'audioStreamPath' | 'videoStreamPath',
  ) => {
    const file = event?.target?.files?.[0];

    if (file) {
      const extension = getFileExtension(file);
      const fileName = getLessonUploadFileName('layer');
      const pathPrefix = source === 'audioStreamPath' ? 'audio/' : 'video/';
      const path = pathPrefix.concat(fileName).concat('.', extension || '');
      await onUploadPrivateFile(event, fileName, path, source);
    }
  };

  const selectHandlerForImageFile = async (event: React.ChangeEvent<HTMLInputElement> | undefined) => {
    const file = event?.target?.files?.[0];

    if (file) {
      const extension = getFileExtension(file);
      const fileName = getLessonUploadFileName('layer');
      const path = 'images/'.concat(fileName).concat('.', extension || '');
      await onUploadPublicFile(event, fileName, path);
    }
  };

  const onAccessFileLink = (fileType: 'audio' | 'video' | 'image') => {
    switch (fileType) {
      case 'image':
        const _imageUrl = imageUrl || PUBLIC_BUCKET + imagePath;
        window.open(_imageUrl, '_blank');
        break;
      case 'audio':
        if (audioFilePath) {
          getPresignedUrl(audioFilePath, (url) => {
            window.open(url, '_blank');
          });
        }
        break;
      case 'video':
        if (videoFilePath) {
          getPresignedUrl(videoFilePath, (url) => {
            window.open(url, '_blank');
          });
        }
        break;
    }
  };

  const onClearAudioLink = () => {
    setAudioFilePath(undefined);
  };
  const onClearVideoLink = () => {
    setVideoFilePath(undefined);
  };
  const onClearImageLink = () => {
    setImagePath(undefined);
  };

  const onSelectDateFromCalendar = (value: Date | null) => {
    if (value !== null) {
      setDisplayPublishDate(value);
      const utcDate = new Date(getPastMidnightUTC(value));
      setPublishDate(utcDate);
    }
  };

  const onClearPublishDate = () => {
    setPublishDate(null);
    setDisplayPublishDate(null);
  };

  const _onSubmit = async (values: FormikValues) => {
    const isDailySession = !!values?.isDailyAlux;
    // The poll is optional so lessons can be saved in segments, but a partially filled one
    // is still rejected - a question without options (or the reverse) is not publishable.
    const hasPoll = isDailySession && !isPollEmpty({ question: pollQuestion, options: pollOptions });

    if (hasPoll) {
      const _pollErrors = validatePoll({ question: pollQuestion, options: pollOptions });
      setPollErrors(_pollErrors);

      // Existing daily sessions may predate this field, so only new ones must fill it in.
      const _listenReasonError =
        isNewLesson && !values?.listenReason?.trim() ? 'Reason why to listen is a required field' : undefined;
      setListenReasonError(_listenReasonError);

      if (_pollErrors || _listenReasonError) {
        return;
      }
    } else {
      setPollErrors(undefined);
      setListenReasonError(undefined);
    }

    await onSave?.({
      ...values,
      keyTakeaways,
      publishDate,
      // `null` tells the API layer to remove any previously stored poll.
      poll: hasPoll ? buildPollForSave({ question: pollQuestion, options: pollOptions }) : null,
      audioStreamPath: audioFilePath,
      videoStreamPath: videoFilePath,
      imagePath: imagePath,
      category: categoryDropdownProps?.selectedItems?.map((item) => item.value)?.[0],
      pillarType: pillarTypeDropdownProps?.selectedItems?.map((item) => item.value)?.[0],
      pillarExpertise: pillarExpertiseDropdownProps?.selectedItems?.map((item) => item.value)?.[0],
      tags: tagsDropdownProps?.selectedItems?.map((item) => item.value) || [],
      // Fixed per environment rather than chosen by the admin, and only meaningful
      // for a daily session.
      postToNetworkId: isDailySession ? DAILY_SESSION_NETWORK_ID : undefined,
    });
  };

  return (
    <Formik
      initialValues={modalState ? lessonInitialValues : { ...initialValues, ...defaultValues }}
      validationSchema={MinimalLessonSchema}
      onSubmit={_onSubmit}>
      {({ values, handleChange, errors, touched, setFieldValue }) => {
        setFormikValues(values);
        return (
          <Components.Form>
            <Components.ModalBodyContainer>
              <ContentInsightsCard content={formState} contentType={'lesson'} cardStyle={'detailed'} />
              <Components.Section>
                <Components.SectionHeader>
                  <Components.SectionTitleRow>
                    <Components.SectionTitle>Basics</Components.SectionTitle>
                    <Components.SectionBadge tone={'required'}>Required</Components.SectionBadge>
                  </Components.SectionTitleRow>
                  <Components.SectionDescription>
                    The minimum needed to save this lesson as a draft.
                  </Components.SectionDescription>
                </Components.SectionHeader>

                <Components.FieldsWrapper>
                  <Components.FieldStartAdornment variant={'minimal'} />
                  <Components.FieldsContent>
                    <Components.FieldWrapper>
                      <Components.FieldLabel>Title</Components.FieldLabel>
                      <ProjectTextField
                        value={values.title}
                        onChangeText={handleChange}
                        name={'title'}
                        errorMessage={errors.title && touched.title ? errors?.title : undefined}
                        placeholder={'Enter lesson title'}
                      />
                    </Components.FieldWrapper>

                    <Components.FieldWrapper>
                      <Components.FieldLabel>
                        Reason Why to Listen the session?
                        {values.isDailyAlux && isNewLesson && <Components.RequiredMark>*</Components.RequiredMark>}
                      </Components.FieldLabel>
                      <ProjectTextField
                        value={values.listenReason}
                        onChangeText={handleChange}
                        name={'listenReason'}
                        errorMessage={listenReasonError}
                        placeholder={'Enter the reason to listen to this session'}
                      />
                    </Components.FieldWrapper>
                  </Components.FieldsContent>
                </Components.FieldsWrapper>
              </Components.Section>

              <Components.Section>
                <Components.SectionHeader>
                  <Components.SectionTitleRow>
                    <Components.SectionTitle>Media</Components.SectionTitle>
                    <Components.SectionBadge tone={'required'}>Needed to play</Components.SectionBadge>
                  </Components.SectionTitleRow>
                  <Components.SectionDescription>
                    An audio or video source and its length. Required before the lesson can be played.
                  </Components.SectionDescription>
                </Components.SectionHeader>

                <Components.FieldsWrapper>
                  <Components.FieldStartAdornment variant={'readyToPlay'} />
                  <Components.FieldsContent>
                    <Components.FieldWrapper>
                      <Components.FieldLabel>Streaming source</Components.FieldLabel>
                      <Components.UploadFileWrapper>
                        <Components.InputMenu
                          type='file'
                          accept={'audio/mp3'}
                          id='audioFile'
                          onChange={(event) => selectHandlerForPrivateFile(event, 'audioStreamPath')}
                        />
                        <ProjectTextField value={audioFilePath || ''} disabled placeholder={'No audio stream path'} />
                        {audioFilePath ? (
                          <>
                            <Components.IconWrapper onClick={() => onAccessFileLink('audio')}>
                              <AccessLinkIcon />
                            </Components.IconWrapper>
                            <Components.ClearButton onClick={onClearAudioLink}>Clear</Components.ClearButton>
                          </>
                        ) : (
                          <Components.UploadButton htmlFor={'audioFile'}>
                            {typeof audioUploadProgress === 'number' ? `${audioUploadProgress}%` : 'Upload Audio'}
                          </Components.UploadButton>
                        )}
                      </Components.UploadFileWrapper>
                      <Components.UploadFileWrapper>
                        <Components.InputMenu
                          type='file'
                          accept={'video/*'}
                          id='videoFile'
                          onChange={(event) => selectHandlerForPrivateFile(event, 'videoStreamPath')}
                        />
                        <ProjectTextField value={videoFilePath || ''} disabled placeholder={'No video stream path'} />
                        {videoFilePath ? (
                          <>
                            <Components.IconWrapper onClick={() => onAccessFileLink('video')}>
                              <AccessLinkIcon />
                            </Components.IconWrapper>
                            <Components.ClearButton onClick={onClearVideoLink}>Clear</Components.ClearButton>
                          </>
                        ) : (
                          <Components.UploadButton htmlFor={'videoFile'}>
                            {typeof videoUploadProgress === 'number' ? `${videoUploadProgress}%` : 'Upload Video'}
                          </Components.UploadButton>
                        )}
                      </Components.UploadFileWrapper>
                    </Components.FieldWrapper>

                    <Components.FieldWrapper>
                      <Components.FieldLabel>Total length</Components.FieldLabel>
                      <Components.LengthInputsWrapper>
                        <ProjectTextField
                          value={values.lengthMinutes}
                          onChangeText={handleChange}
                          name={'lengthMinutes'}
                          errorMessage={
                            errors.lengthMinutes && touched.lengthMinutes ? errors?.lengthMinutes : undefined
                          }
                          placeholder={'Enter the number of minutes'}
                          {...textFieldUtils.inputPositiveNumber}
                        />
                        <ProjectTextField
                          value={values.lengthSeconds}
                          onChangeText={handleChange}
                          name={'lengthSeconds'}
                          errorMessage={
                            errors.lengthSeconds && touched.lengthSeconds ? errors?.lengthSeconds : undefined
                          }
                          placeholder={'Enter the number of seconds'}
                          {...textFieldUtils.inputPositiveNumber}
                        />
                      </Components.LengthInputsWrapper>
                    </Components.FieldWrapper>

                    <LessonTranscriptSection
                      audioFilePath={audioFilePath}
                      isAudioUploading={typeof audioUploadProgress === 'number'}
                      lrcTranscript={values.lrcTranscript}
                      onChangeLrcTranscript={(value) => setFieldValue('lrcTranscript', value)}
                      onGenerated={(lrc) => setFieldValue('lrcTranscript', lrc)}
                      isModalOpen={isModalOpen}
                    />
                  </Components.FieldsContent>
                </Components.FieldsWrapper>
              </Components.Section>

              <Components.Section>
                <Components.SectionHeader>
                  <Components.SectionTitleRow>
                    <Components.SectionTitle>Details &amp; scheduling</Components.SectionTitle>
                    <Components.SectionBadge tone={'optional'}>Optional</Components.SectionBadge>
                  </Components.SectionTitleRow>
                  <Components.SectionDescription>
                    Artwork, classification and publish date. These improve discovery but never block saving.
                  </Components.SectionDescription>
                </Components.SectionHeader>

                <Components.FieldsWrapper>
                  <Components.FieldStartAdornment variant={'additional'} />
                  <Components.FieldsContent>
                    <Components.FieldWrapper>
                      <Components.FieldWrapperWithInsight>
                        <Components.FieldLabel>Image</Components.FieldLabel>
                        <ContentInsightsCard
                          content={formState}
                          fieldName={'imagePath'}
                          contentType={'lesson'}
                          cardStyle={'fieldAdornment'}
                        />
                      </Components.FieldWrapperWithInsight>
                      <Components.UploadFileWrapper>
                        <Components.InputMenu
                          type='file'
                          accept={'image/*'}
                          id='imageFile'
                          onChange={selectHandlerForImageFile}
                        />
                        <ProjectTextField value={imagePath || ''} disabled placeholder={'No image path'} />
                        {imagePath ? (
                          <>
                            <Components.IconWrapper onClick={() => onAccessFileLink('image')}>
                              <AccessLinkIcon />
                            </Components.IconWrapper>
                            <Components.ClearButton onClick={onClearImageLink}>Clear</Components.ClearButton>
                          </>
                        ) : (
                          <Components.UploadButton htmlFor={'imageFile'}>
                            {typeof imageUploadProgress === 'number' ? `${imageUploadProgress}%` : 'Upload Image'}
                          </Components.UploadButton>
                        )}
                      </Components.UploadFileWrapper>
                    </Components.FieldWrapper>

                    <Components.FieldWrapper>
                      <Components.FieldWrapperWithInsight>
                        <Components.FieldLabel>Category</Components.FieldLabel>
                        <ContentInsightsCard
                          content={formState}
                          fieldName={'category'}
                          contentType={'lesson'}
                          cardStyle={'fieldAdornment'}
                        />
                      </Components.FieldWrapperWithInsight>
                      <Components.Picker onClick={(e) => categoryDropdownProps.show(e)}>
                        {categoryDropdownProps?.selectedItems && categoryDropdownProps?.selectedItems?.length > 0 ? (
                          <Components.PickerLabel>
                            {getTextFromSelectedItems(categoryDropdownProps?.selectedItems)}
                          </Components.PickerLabel>
                        ) : (
                          <Components.NotSetLabel>Not set</Components.NotSetLabel>
                        )}
                        <Components.PickerIconWrapper>
                          <Components.PickerIcon />
                        </Components.PickerIconWrapper>
                      </Components.Picker>
                      <ProjectSearchDropdown
                        {...categoryDropdownProps}
                        facetFilter={(item) => possibleCategories.includes(item.value)}
                      />
                    </Components.FieldWrapper>

                    <Components.FieldWrapper>
                      <Components.LengthInputsWrapper>
                        <Components.FieldWrapperWithInsight>
                          <Components.FieldLabel>Pillar</Components.FieldLabel>
                          <ContentInsightsCard
                            content={formState}
                            fieldName={'pillarType'}
                            contentType={'lesson'}
                            cardStyle={'fieldAdornment'}
                          />
                        </Components.FieldWrapperWithInsight>

                        <Components.FieldWrapperWithInsight>
                          <Components.FieldLabel>Expertise</Components.FieldLabel>
                          <ContentInsightsCard
                            content={formState}
                            fieldName={'pillarExpertise'}
                            contentType={'lesson'}
                            cardStyle={'fieldAdornment'}
                          />
                        </Components.FieldWrapperWithInsight>
                      </Components.LengthInputsWrapper>

                      <Components.LengthInputsWrapper>
                        <Components.Picker onClick={(e) => pillarTypeDropdownProps.show(e)}>
                          {pillarTypeDropdownProps?.selectedItems &&
                          pillarTypeDropdownProps?.selectedItems?.length > 0 ? (
                            <Components.PickerLabel>
                              {getTextFromSelectedItems(pillarTypeDropdownProps?.selectedItems)}
                            </Components.PickerLabel>
                          ) : (
                            <Components.NotSetLabel>Not set</Components.NotSetLabel>
                          )}
                          <Components.PickerIconWrapper>
                            <Components.PickerIcon />
                          </Components.PickerIconWrapper>
                        </Components.Picker>
                        <ProjectSearchDropdown
                          {...pillarTypeDropdownProps}
                          facetFilter={(item) => possiblePillars.includes(item.value)}
                        />
                        <Components.Picker onClick={(e) => pillarExpertiseDropdownProps.show(e)}>
                          {pillarExpertiseDropdownProps?.selectedItems &&
                          pillarExpertiseDropdownProps?.selectedItems?.length > 0 ? (
                            <Components.PickerLabel>
                              {getTextFromSelectedItems(pillarExpertiseDropdownProps?.selectedItems)}
                            </Components.PickerLabel>
                          ) : (
                            <Components.NotSetLabel>Not set</Components.NotSetLabel>
                          )}
                          <Components.PickerIconWrapper>
                            <Components.PickerIcon />
                          </Components.PickerIconWrapper>
                        </Components.Picker>
                        <ProjectSearchDropdown {...pillarExpertiseDropdownProps} />
                      </Components.LengthInputsWrapper>
                    </Components.FieldWrapper>

                    <Components.FieldWrapper>
                      <Components.FieldLabel>Tags</Components.FieldLabel>
                      <Components.Picker onClick={(e) => tagsDropdownProps.show(e)}>
                        {tagsDropdownProps?.selectedItems && tagsDropdownProps?.selectedItems?.length > 0 ? (
                          <Components.PickerLabel>
                            {getTextFromSelectedItems(tagsDropdownProps?.selectedItems)}
                          </Components.PickerLabel>
                        ) : (
                          <Components.NotSetLabel>Not set</Components.NotSetLabel>
                        )}
                        <Components.PickerIconWrapper>
                          <Components.PickerIcon />
                        </Components.PickerIconWrapper>
                      </Components.Picker>
                      <ProjectSearchDropdown {...tagsDropdownProps} />
                    </Components.FieldWrapper>

                    <Components.FieldWrapper>
                      <Components.FieldWrapperWithInsight>
                        <Components.FieldLabel>Key Takeaways</Components.FieldLabel>
                        <ContentInsightsCard
                          content={formState}
                          fieldName={'keyTakeaways'}
                          contentType={'lesson'}
                          cardStyle={'fieldAdornment'}
                        />
                      </Components.FieldWrapperWithInsight>
                      <AddKeyTakeaways data={keyTakeaways} setData={setKeyTakeaways} />
                    </Components.FieldWrapper>

                    <Components.CheckboxFieldWrapper>
                      <Components.Checkbox
                        checked={values.isQuestionAndAnswer}
                        onChange={handleChange}
                        name={'isQuestionAndAnswer'}
                      />
                      <Components.CheckboxLabel>Is Question & Answer</Components.CheckboxLabel>
                    </Components.CheckboxFieldWrapper>

                    <Components.FieldWrapper>
                      <Components.FieldLabel>Published at</Components.FieldLabel>
                      <ProjectCalendarPicker
                        dateValue={displayPublishDate}
                        onDateValueChange={onSelectDateFromCalendar}
                        valueDisplayFormat={'day'}
                      />
                      {publishDate && (
                        <Components.ClearButton onClick={() => onClearPublishDate()}>
                          Clear publish date
                        </Components.ClearButton>
                      )}
                    </Components.FieldWrapper>
                  </Components.FieldsContent>
                </Components.FieldsWrapper>
              </Components.Section>

              <Components.Section>
                <Components.SectionHeader>
                  <Components.SectionTitleRow>
                    <Components.SectionTitle>Daily session</Components.SectionTitle>
                    {values.isDailyAlux && (
                      <Components.SectionBadge tone={'optional'}>Optional</Components.SectionBadge>
                    )}
                  </Components.SectionTitleRow>
                  <Components.SectionDescription>
                    {values.isDailyAlux
                      ? 'Daily sessions publish with a Social+ post, and optionally a poll. Leave the poll blank to add it later.'
                      : 'Mark this lesson as a daily session to publish it with a Social+ post and an optional poll.'}
                  </Components.SectionDescription>
                </Components.SectionHeader>

                <Components.CheckboxFieldWrapper>
                  <Components.Checkbox checked={values.isDailyAlux} onChange={handleChange} name={'isDailyAlux'} />
                  <Components.CheckboxLabel>Is Daily Session</Components.CheckboxLabel>
                </Components.CheckboxFieldWrapper>

                {values.isDailyAlux ? (
                  <Components.LinkedRequirementCard>
                    <Components.FieldWrapper>
                      <Components.FieldLabel>
                        Daily Poll<Components.OptionalMark>(optional)</Components.OptionalMark>
                      </Components.FieldLabel>
                      <AddPoll
                        lessonId={modalState?.id}
                        question={pollQuestion}
                        setQuestion={setPollQuestion}
                        options={pollOptions}
                        setOptions={setPollOptions}
                        lockedOptionsCount={lockedOptionsCount}
                        errors={pollErrors}
                      />
                    </Components.FieldWrapper>

                    <Components.FieldWrapper>
                      <Components.FieldLabel>
                        Social+ network Post<Components.RequiredMark>*</Components.RequiredMark>
                      </Components.FieldLabel>
                      <Components.ReadOnlyField>
                        {DAILY_SESSION_NETWORK_NAME || DAILY_SESSION_NETWORK_ID ? (
                          <Components.ReadOnlyValue>
                            {DAILY_SESSION_NETWORK_NAME || DAILY_SESSION_NETWORK_ID}
                          </Components.ReadOnlyValue>
                        ) : (
                          <Components.NotSetLabel>Not configured for this environment</Components.NotSetLabel>
                        )}
                      </Components.ReadOnlyField>
                      <Components.FieldHint>
                        {DAILY_SESSION_NETWORK_ID
                          ? 'Set per environment - every daily session posts to this network.'
                          : 'No network is configured, so no post will be created.'}
                      </Components.FieldHint>
                    </Components.FieldWrapper>

                    <Components.FieldWrapper>
                      <Components.FieldLabel>Post title</Components.FieldLabel>
                      <ProjectTextField
                        value={values.postTitle}
                        onChangeText={handleChange}
                        name={'postTitle'}
                        placeholder={buildDefaultPostTitle(values.title) || 'Daily Alux: {lesson title}'}
                      />
                      <Components.FieldHint>Leave blank to use the default title shown above.</Components.FieldHint>
                    </Components.FieldWrapper>

                    <Components.FieldWrapper>
                      <Components.FieldLabel>Post content</Components.FieldLabel>
                      <Components.TextArea
                        value={values.postContent}
                        onChange={handleChange}
                        name={'postContent'}
                        placeholder={'Add optional post content'}
                      />
                    </Components.FieldWrapper>
                  </Components.LinkedRequirementCard>
                ) : null}
              </Components.Section>
            </Components.ModalBodyContainer>
            <Components.ModalButtonsContainer>
              <ContentInsightsCard content={formState} contentType={'lesson'} cardStyle={'small'} />
              <Components.ModalTertiaryButton onClick={() => onCancel?.()}>Cancel</Components.ModalTertiaryButton>
              <Components.ModalPrimaryButton type={'submit'}>Save</Components.ModalPrimaryButton>
            </Components.ModalButtonsContainer>
          </Components.Form>
        );
      }}
    </Formik>
  );
};

const CreateOrEditLessonModal = ({
  isModalOpen,
  onModalClose,
  modalState,
  refreshCallback,
  defaultValues,
  onLessonCreated,
}: {
  isModalOpen: boolean;
  onModalClose: (refreshTrigger?: boolean) => void;
  modalState?: { lesson?: Lesson; publishedAtDate?: Date };
  refreshCallback?: () => Promise<void>;
  defaultValues?: LessonDefaultValues;
  onLessonCreated?: (id: string) => void | Promise<void>;
}) => {
  const { setIsLoading } = useContext(ActivityIndicatorContext);
  const { showErrorToast, showSuccessToast } = useContext(ToastContext);

  const confirmationModal = useModalState<boolean>();

  const _onModalClose = (refreshTrigger?: boolean) => {
    confirmationModal.open(refreshTrigger || false);
  };

  const _onSave = async (values: FormikValues) => {
    const _lengthMinutes =
      typeof values?.lengthMinutes === 'string' ? parseInt(values?.lengthMinutes) : values?.lengthMinutes;
    const _lengthSeconds =
      typeof values?.lengthSeconds === 'string' ? parseInt(values?.lengthSeconds) : values?.lengthSeconds;
    const _totalLength = _lengthMinutes > 0 ? _lengthMinutes * 60 + _lengthSeconds : _lengthSeconds;
    // The backend needs a concrete title to post with, so fall back to the default
    // template rather than leaving it to interpret a blank field.
    const _postTitle = values?.postTitle?.trim()?.length
      ? values?.postTitle?.trim()
      : buildDefaultPostTitle(values?.title);
    const data: CreateLessonDto = {
      title: values?.title,
      ...(_totalLength && { totalLength: _totalLength }),
      category: values?.category?.length ? values?.category : null,
      pillarType: values?.pillarType?.length ? values?.pillarType : null,
      pillarExpertise: values?.pillarExpertise?.length ? values?.pillarExpertise : null,
      publishedAt: values?.publishDate ? values?.publishDate?.getTime() : null,
      groupType: values?.isDailyAlux ? 'layer' : modalState?.lesson?.groupType,
      isDailyLesson: values?.isDailyAlux || false,
      isQuestionAndAnswer: values?.isQuestionAndAnswer || false,
      keyTakeaways: values?.keyTakeaways && values?.keyTakeaways?.length ? values?.keyTakeaways : null,
      videoStreamPath: values?.videoStreamPath?.length ? values?.videoStreamPath : null,
      audioStreamPath: values?.audioStreamPath?.length ? values?.audioStreamPath : null,
      imagePath: values?.imagePath?.length ? values?.imagePath : null,
      contentType: 'lesson',
      otherCategories: modalState?.lesson?.otherCategories || [],
      tags: values?.tags || [],
      lrcTranscript: values?.lrcTranscript?.length ? normalizeLrc(values.lrcTranscript) : null,
      listenReason: values?.listenReason?.length ? values.listenReason : null,
      // Post details only belong to a daily session - `null` clears them otherwise.
      postToNetworkId: values?.isDailyAlux && values?.postToNetworkId?.length ? values?.postToNetworkId : null,
      postTitle: values?.isDailyAlux && _postTitle?.length ? _postTitle : null,
      postContent: values?.isDailyAlux && values?.postContent?.length ? values?.postContent : null,
      poll: values?.poll ?? null,
    };

    setIsLoading(true);
    const result =
      modalState && modalState?.lesson?.id
        ? await updateLesson({ ...data, id: modalState?.lesson?.id })
        : await createLesson(data);
    setIsLoading(false);

    const resultError = isProjectError(result);
    if (resultError) {
      showErrorToast(resultError);
    } else {
      modalState && modalState?.lesson?.id
        ? showSuccessToast('The changes were saved successfully')
        : showSuccessToast('You successfully added a lesson');
      const createdId = (result as { id?: string }).id;
      if (!modalState?.lesson?.id && createdId) {
        await onLessonCreated?.(createdId);
      }
      onModalClose?.(true);
      refreshCallback?.().then();
    }
  };

  const onConfirmCancel = (value: boolean) => {
    onModalClose?.(value);
  };

  return (
    <>
      <ProjectModal
        bodyChildren={
          <ModalBody
            modalState={modalState?.lesson}
            publishedAtDate={modalState?.publishedAtDate}
            defaultValues={defaultValues}
            onSave={_onSave}
            onCancel={_onModalClose}
            isModalOpen={isModalOpen}
          />
        }
        isModalOpen={isModalOpen}
        onModalClose={_onModalClose}
        modalTitle={modalState?.lesson ? `Edit lesson` : 'Create new lesson'}
        titleSuffix={modalState?.lesson ? `(ID: ${modalState?.lesson?.id})` : undefined}
        disableCloseOnClickAway
      />
      <ConfirmationModal
        {...confirmationModal}
        confirmationText={confirmationTexts.lessons.cancelEditing}
        onConfirm={onConfirmCancel}
      />
    </>
  );
};

export default CreateOrEditLessonModal;
